import i18next from "i18next";
import { type InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";
import { Backend, BACKEND_SENTINEL } from "./backend";
import { unmaskString } from "./mask";
import { emitTranslationLoadError } from "./events";
import { captureException } from "@web-monorepo/telemetry";

export const i18n = i18next;

i18n.use(Backend);

i18n.on("failedLoading", (lng: string, ns: string, msg: string) => {
  const isFromBackend = msg === BACKEND_SENTINEL;

  if (!isFromBackend) {
    // Error didn't come from our backend, report it
    captureException(new Error(msg), {
      tags: { language: lng, namespace: ns },
    });
  }

  // Emit a reasonable message for consumers (not the sentinel)
  const errorMessage = isFromBackend ? "Failed to load translations" : msg;
  emitTranslationLoadError(lng, ns, errorMessage);
});

const FALLBACKS: Record<string, string[]> = {
  "es-MX": ["es-ES"], // Mexican Spanish
  "es-ES": ["es-MX"], // Spanish Spanish
  es: ["es-MX", "es-ES"], // Generalized spanish
  "fr-CA": ["fr-FR"], // Canadian French
  "fr-FR": ["fr-CA"], // French French
  "zh-CN": ["zh-HK", "zh-TW"],
  "zh-TW": ["zh-HK", "zh-CN"],
  "zh-HK": ["zh-TW", "zh-CN"],
  ca: ["es-ES", "es-MX"], // Catalan
  default: [],
};

const SUPPORTED_SPANISH = ["es-MX", "es-ES"];

export function mapLocaleToI18nLocale(locale: string) {
  if (locale === "en-US") {
    // en-US and en are actually the same, because the source of the translations ("en")
    // is written by people in the US. Rewrite requests from "en-US" to "en" so we don't
    // fetch two bundles with the same data. Also because test environments only know how
    // to serve "en".
    return "en";
  }

  if ((locale === "es" || locale.startsWith("es-")) && !SUPPORTED_SPANISH.includes(locale)) {
    return SUPPORTED_SPANISH[0];
  }

  return locale;
}

// for all options read: https://www.i18next.com/overview/configuration-options
const i18nOptions: InitOptions = {
  defaultNS: "dojo.common",
  fallbackLng: FALLBACKS,

  debug: false,
  // IMPORTANT: saveMissing MUST be true to trigger missingKeyHandler,
  // which registers fallback strings for Sentry replay unmasking (see below).
  // Changing this to false will break the masking feature.
  saveMissing: true,
  saveMissingPlurals: false,

  missingKeyHandler: (
    _lngs: readonly string[],
    _ns: string,
    _key: string,
    fallbackValue: string,
    _updateMissing: boolean,
    _options: unknown,
  ) => {
    if (!fallbackValue) return;

    unmaskString(fallbackValue);
    return fallbackValue;
  },

  updateMissing: false,

  react: { useSuspense: true },
  interpolation: {
    prefix: "__",
    suffix: "__",
    escapeValue: false, // not needed for react as it escapes by default
  },
};

const namespaces = new Set<string>();

function addNamespaces(ns?: string | readonly string[]) {
  if (typeof ns === "string") namespaces.add(ns);
  else if (Array.isArray(ns)) ns.forEach((n) => namespaces.add(n));
}

export function initialize(options: InitOptions) {
  i18n.use(initReactI18next);

  addNamespaces(options.ns);
  addNamespaces(i18nOptions.ns);

  return i18n.init({
    lng: (typeof navigator !== "undefined" && mapLocaleToI18nLocale(navigator.language)) || "en",
    ...i18nOptions,
    ...options,
    backend: {
      ...i18nOptions.backend,
      ...options.backend,
    },
    ns: [...namespaces.keys()],
  });
}

export function loadNamespaces(ns: string | readonly string[]) {
  addNamespaces(ns);
  return i18n.loadNamespaces(ns);
}
